import React from 'react';
import { getStatus } from './assignmentStatus';
import { periodText, ruleText, statusText, statusTone } from './assignmentText';

const S = {
  marketerCard: (activeDrop) => ({
    border: activeDrop ? '2px solid #2563eb' : '1px solid var(--border)',
    borderRadius: 12,
    background: activeDrop ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)',
    overflow: 'hidden',
    minWidth: 0,
    boxShadow: activeDrop ? '0 0 0 3px rgba(37,99,235,0.14)' : 'none',
  }),
  marketerHead: {
    padding: '9px 10px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg-subtle)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  marketerName: { fontSize: 13, fontWeight: 950, color: 'var(--text)' },
  marketerMeta: { marginTop: 2, fontSize: 10, color: 'var(--text-muted)', fontWeight: 800 },
  headActions: { display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' },
  addBtn: { height: 32, border: 'none', borderRadius: 8, padding: '0 10px', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 900, cursor: 'pointer' },
  smallBtn: { height: 32, border: '1px solid var(--border)', borderRadius: 8, padding: '0 9px', background: 'var(--bg-card)', color: 'var(--text-sub)', fontSize: 12, fontWeight: 800, cursor: 'pointer' },
  danger: { borderColor: 'rgba(220,38,38,0.25)', color: '#dc2626', background: 'rgba(220,38,38,0.06)' },
  list: { padding: 8, display: 'flex', flexDirection: 'column', gap: 6 },
  assignment: (dragging) => ({
    border: '1px solid var(--border)',
    borderRadius: 9,
    background: 'var(--bg-subtle)',
    padding: '7px 8px',
    minWidth: 0,
    opacity: dragging ? 0.45 : 1,
    cursor: 'pointer',
  }),
  assignmentTop: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 7, alignItems: 'start' },
  consultant: { minWidth: 0, fontSize: 12, fontWeight: 950, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  note: { marginTop: 2, fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  period: { marginTop: 5, fontSize: 11, color: 'var(--text-sub)', fontWeight: 850 },
  rule: { marginTop: 3, fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  openHint: { marginTop: 5, fontSize: 11, fontWeight: 800, color: '#2563eb', textAlign: 'right' },
  dropHint: { marginTop: 5, fontSize: 11, fontWeight: 950, color: '#2563eb' },
  badge: (status) => ({
    flex: '0 0 auto',
    borderRadius: 999,
    padding: '2px 7px',
    background: status === 'active' ? 'rgba(22,163,74,0.12)' : status === 'upcoming' ? 'rgba(37,99,235,0.12)' : 'rgba(100,116,139,0.12)',
    color: status === 'active' ? '#15803d' : status === 'upcoming' ? '#2563eb' : '#64748b',
    fontSize: 10,
    fontWeight: 950,
    whiteSpace: 'nowrap',
  }),
  empty: { padding: 14, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 },
};

function sortAssignments(rows, range) {
  const order = { active: 0, upcoming: 1, ended: 2 };
  return [...rows].sort((a, b) => (
    order[getStatus(a, range).key] - order[getStatus(b, range).key]
    || String(a.start_date || '').localeCompare(String(b.start_date || ''))
    || String(a.consultant_name || '').localeCompare(String(b.consultant_name || ''), 'ko')
  ));
}

export function MarketerCard({
  marketer,
  rows,
  range,
  today,
  onAdd,
  onOpen,
  onHide,
  onDropItem,
  onDragStart,
  onDragEnd,
  dragItem,
  dragOverMarketerId,
  setDragOverMarketerId,
  saving,
}) {
  const sorted = sortAssignments(rows, range);
  const counts = sorted.reduce((acc, row) => {
    acc[getStatus(row, range).key] += 1;
    return acc;
  }, { active: 0, upcoming: 0, ended: 0 });
  const canDrop = dragItem && (
    dragItem.type === 'consultant'
    || (dragItem.type === 'assignment' && Number(dragItem.assignment.marketer_user_id) !== Number(marketer.id))
  );
  const activeDrop = canDrop && Number(dragOverMarketerId) === Number(marketer.id);

  function handleDrop(event) {
    if (!canDrop) return;
    event.preventDefault();
    setDragOverMarketerId(null);
    onDropItem(marketer);
  }

  return (
    <section
      style={S.marketerCard(activeDrop)}
      onDragOver={(event) => {
        if (!canDrop) return;
        event.preventDefault();
        setDragOverMarketerId(marketer.id);
      }}
      onDragLeave={() => activeDrop && setDragOverMarketerId(null)}
      onDrop={handleDrop}
    >
      <div style={S.marketerHead}>
        <div style={{ minWidth: 0 }}>
          <div style={S.marketerName}>{marketer.name}</div>
          <div style={S.marketerMeta}>1:1 직원 {counts.active}명 · 예정 {counts.upcoming}명</div>
          {activeDrop && <div style={S.dropHint}>놓으면 {marketer.name}에게 배정</div>}
        </div>
        <div style={S.headActions}>
          <button type="button" style={S.addBtn} onClick={() => onAdd(marketer)}>+ 직원 배정</button>
          <button type="button" style={S.smallBtn} onClick={() => onHide(marketer.id)}>숨김</button>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div style={S.empty}>배정 없음</div>
      ) : (
        <div style={S.list}>
          {sorted.map((row) => {
            const status = getStatus(row, range);
            const dragging = dragItem?.type === 'assignment' && Number(dragItem.assignment.id) === Number(row.id);
            return (
              <div
                key={row.id}
                role="button"
                tabIndex={0}
                style={S.assignment(dragging)}
                onClick={() => onOpen(row)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  onOpen(row);
                }}
                draggable={!saving && status.key !== 'ended'}
                onDragStart={(event) => {
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', `assignment:${row.id}`);
                  onDragStart({ type: 'assignment', assignment: row });
                }}
                onDragEnd={onDragEnd}
              >
                <div style={S.assignmentTop}>
                  <div style={{ minWidth: 0 }}>
                    <div style={S.consultant}>{row.consultant_name}</div>
                    {row.notes && <div style={S.note} title={row.notes}>{row.notes}</div>}
                  </div>
                  <span style={S.badge(statusTone(row, today))}>{statusText(row, today)}</span>
                </div>
                <div style={S.period}>{periodText(row)}</div>
                <div style={S.rule} title={ruleText(row)}>{ruleText(row)}</div>
                <div style={S.openHint}>눌러서 담당 바꾸기·끝내기·고치기 ›</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
